import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

type GuestModeContextType = {
  isGuestMode: boolean;
  setGuestMode: (value: boolean) => void;
  showAuthModal: boolean;
  setShowAuthModal: (value: boolean) => void;
  requireAuth: (featureName?: string) => boolean;
  pendingFeature: string | null;
};

const GuestModeContext = createContext<GuestModeContextType>({
  isGuestMode: false,
  setGuestMode: () => {},
  showAuthModal: false,
  setShowAuthModal: () => {},
  requireAuth: () => true,
  pendingFeature: null,
});

export const useGuestMode = () => {
  const context = useContext(GuestModeContext);
  if (!context) {
    throw new Error('useGuestMode must be used within a GuestModeProvider');
  }
  return context;
};

export const GuestModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingFeature, setPendingFeature] = useState<string | null>(null);
  
  // Get auth state from Redux
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.user);
  
  // Clear guest mode when user signs in
  useEffect(() => {
    if (isAuthenticated && isGuestMode) {
      setIsGuestMode(false);
      setShowAuthModal(false);
      setPendingFeature(null);
    }
  }, [isAuthenticated, isGuestMode]);
  
  // Utility function to check auth and show modal if needed
  const requireAuth = useCallback((featureName?: string): boolean => {
    if (isAuthenticated) {
      return true; // User is authenticated, allow action
    }
    
    // User is not authenticated (guest), show modal
    setPendingFeature(featureName || null);
    setShowAuthModal(true);
    return false;
  }, [isAuthenticated]);

  const setGuestMode = useCallback((value: boolean) => {
    setIsGuestMode(value);
    if (!value) {
      // Clear modal state when exiting guest mode
      setShowAuthModal(false);
      setPendingFeature(null);
    }
  }, []);

  return (
    <GuestModeContext.Provider 
      value={{ 
        isGuestMode, 
        setGuestMode, 
        showAuthModal, 
        setShowAuthModal,
        requireAuth,
        pendingFeature,
      }}
    >
      {children}
    </GuestModeContext.Provider>
  );
};
